import type { TextFieldProps } from '@mui/material/TextField';
import {
	DatePickerProps,
	DateValidationError,
	PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import {
	FieldPath,
	FieldValues,
	UseControllerProps,
	useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '../datetpickers/DatePicker';
import { displayError, getHelperText } from './functions';

export type DatePickerElementProps<
	TDate extends DateTime<boolean>,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<DatePickerProps, 'value' | 'onChange'> &
	Pick<UseControllerProps<TFieldValues, TName>, 'name' | 'control'> & {
		required?: boolean;
		validation?: UseControllerProps<TFieldValues, TName>['rules'];
		onChange?: (
			value: TDate | null,
			context: PickerChangeHandlerContext<DateValidationError>
		) => void;
		helperText?: TextFieldProps['helperText'];
		size?: TextFieldProps['size'];
		fullWidth?: boolean;
	};

/**
 * Controlled date picker, to be used with react-hook-form forms.
 *
 * TODO: - In current implementation date picker does not sets errors to form, that are incoming from its own validation.
 * This means, that disableFuture or disablePast will not validate values.
 *
 * @param {number} name Name of the form field
 */
export const ControlledDatePicker = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	name,
	required,
	validation = {},
	control,
	fullWidth = false,
	disabled,
	label,
	size,
	slotProps: {
		textField = { variant: 'outlined' as const },
		...restSlotProps
	} = {},
	helperText,
	...rest
}: DatePickerElementProps<
	DateTime,
	TFieldValues,
	TName
>): React.JSX.Element => {
	// Removed destructuring of onClick as it does not exist on the type
	const { t } = useTranslation();

	if (Boolean(validation.required) && !required) {
		required = true;
	}

	const {
		field: { value, ref: controllerRef, ...field },
		fieldState: { error },
	} = useController<TFieldValues, TName>({
		name,
		control,
		rules: {
			...validation,
		},
	});

	return (
		<DatePicker
			slotProps={{
				textField: {
					fullWidth,
					size,
					error: displayError(disabled, error),
					helperText: getHelperText(t, disabled, error, helperText),
					required,
					...textField,
				},
				day: {
					onClick: (event) => {
						event.stopPropagation();
					},
				},
				...restSlotProps,
			}}
			size={size}
			disabled={disabled}
			label={label}
			value={value || null}
			inputRef={controllerRef}
			{...field}
			{...rest}
		/>
	);
};
